import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import * as S from './Description.styles';
import { MAX_LINES, VERY_LARGE_LINE_LIMIT } from './Description.constants';
import { isTextOverflowing } from './Description.helpers';
import { useAdDetails } from '@/hooks/useAdDetails';
import { useIsAuthor } from '@/hooks/useIsAuthor';
import { CopyRestrictedText } from '@/components/CopyRestrictedText/CopyRestrictedText';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { GET_USER } from '@/api/queries/user.queries';
import { GetUserResponse } from '@/api/types/user.types';
import { useServerDataContext } from '@/contexts';
import { useResponsive } from '@/hooks/useResponsive';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { HEADER_HEIGHT } from '@/components/Header/Header.constants';

export const Description = () => {
  const { t } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const isAuthor = useIsAuthor();
  const [overflowing, setOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isDesktop } = useResponsive();
  const [isOpen, toggle] = useToggleOpenState(false);

  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);

  const adData = adDetails ?? initialAdDetails;
  const isB2B = userInfo?.currentUser?.isActiveShop;
  const isCopyDisabled = isB2B && !isAuthor;
  const linesCount = isOpen ? VERY_LARGE_LINE_LIMIT : MAX_LINES;

  const handleTextRef = (node: HTMLParagraphElement | null) => {
    if (node && !isOpen) {
      setOverflowing(isTextOverflowing(node));
    }
  };

  const handleToggleClick = () => {
    const rect = containerRef.current?.getBoundingClientRect();

    if (rect && isOpen) {
      const { top } = rect;
      const openedPosition = top + window.scrollY;
      let targetTop: number | null = null;

      if (isDesktop) {
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;

        if (top < 0 || top > viewportHeight) {
          targetTop = openedPosition;
        }
      } else if (top < 0) {
        targetTop = openedPosition - HEADER_HEIGHT;
      }

      if (targetTop !== null) {
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      }
    }

    toggle();
  };

  if (!adData) {
    return (
      <S.DescriptionContainer>
        <SkeletonLoader
          dataTestId="ad-description-skeleton-loader"
          borderRadius={14}
          height="50px"
        />
      </S.DescriptionContainer>
    );
  }

  if (!adData.body) {
    return null;
  }

  return (
    <S.DescriptionContainer ref={containerRef}>
      <S.DescriptionTextBox $isOpen={isOpen}>
        <CopyRestrictedText
          disableSelection={isCopyDisabled}
          disableRightClick={isCopyDisabled}
          disableCopyEvent={isCopyDisabled}
        >
          <S.DescriptionText
            ref={handleTextRef}
            $variant="2"
            $linesCount={linesCount}
            data-testid="ad-description"
          >
            {adData.body}
          </S.DescriptionText>
        </CopyRestrictedText>
      </S.DescriptionTextBox>
      {overflowing && (
        <S.DescriptionToggle $variant="2" onClick={handleToggleClick}>
          {t(isOpen ? 'common.read_less' : 'common.read_more')}
        </S.DescriptionToggle>
      )}
    </S.DescriptionContainer>
  );
};
